/**
 * Pure decision which interface a login gets (no store / Vue imports, checked in tests/teacherPortal/portalAccess.check.ts).
 * Only the roles (guiAccess of permissioner roles + login) decide, never the flags isEmployee / isTeacher / isApplicant
 * of the employee (they grant nothing):
 * - office: at least one menu key outside PERSONAL_GUI_KEYS (with TEACHER_PORTAL additionally the menu item "Mein Unterricht")
 * - teacher: TEACHER_PORTAL and only personal keys → only the Lehrkraft-Bereich (/my-teaching)
 * - applicant: APPLICANT_PORTAL and only personal keys, or an employee login without any menu key whose employee is an
 *   applicant (old applicant logins without role) → only the Bewerbungs-Bereich (/my-application)
 * - unassigned: employee login without any menu key and not an applicant (no role / nothing granted yet): no office shell,
 *   only the page "Für dich ist in Vanilla noch nichts freigeschaltet" (NO_ACCESS_HOME) and the public / answer pages
 * - none: no employee login (students, parents, partners) – nothing changes for them
 */
export type PortalMode = 'office' | 'teacher' | 'applicant' | 'unassigned' | 'none';

/** Page of a login without any role / menu key */
export const NO_ACCESS_HOME = '/no-access';

/** Same values as NavigationDrawerAccess.TEACHER_PORTAL / APPLICANT_PORTAL */
export const TEACHER_PORTAL_KEY = 'TEACHER_PORTAL';
export const APPLICANT_PORTAL_KEY = 'APPLICANT_PORTAL';

/**
 * Menu keys that only concern the own work of a teacher / applicant. A login whose keys are all in this list is no office login.
 * (DASHBOARD, TODAY, MY_SCHEDULE, MY_PROFILE, MY_APPOINTMENTS, PAYOFF, SUBSTITUTION_INQUIRIES were the menu of the old teacher role.)
 */
export const PERSONAL_GUI_KEYS: readonly string[] = [
    TEACHER_PORTAL_KEY,
    APPLICANT_PORTAL_KEY,
    'DASHBOARD',
    'TODAY',
    'MY_SCHEDULE',
    'MY_PROFILE',
    'MY_APPOINTMENTS',
    'PAYOFF',
    'SUBSTITUTION_INQUIRIES',
];

export const TEACHER_PORTAL_PREFIX = '/my-teaching';
export const TEACHER_PORTAL_HOME = '/my-teaching/today';
export const APPLICANT_PORTAL_PREFIX = '/my-application';
export const APPLICANT_PORTAL_HOME = '/my-application';

export type PortalInput = {
    /** LoginTypes value of the login ('Employee', 'Student', …) */
    userType: string | null | undefined;
    /** Granted menu keys (union of the roles and the login, store.state.guiAccess) */
    guiAccess: readonly string[] | null | undefined;
    /** Employee.isApplicant of the login's employee; only used for employee logins without any menu key */
    isApplicant?: boolean | null;
};

export function resolvePortalMode(input: PortalInput): PortalMode {
    if (input.userType !== 'Employee') {
        return 'none';
    }
    const keys = (input.guiAccess || []) as string[];
    if (keys.some((key) => !PERSONAL_GUI_KEYS.includes(key))) {
        return 'office';
    }
    if (keys.includes(TEACHER_PORTAL_KEY)) {
        return 'teacher';
    }
    if (keys.includes(APPLICANT_PORTAL_KEY) || (!keys.length && !!input.isApplicant)) {
        return 'applicant';
    }
    // nothing granted (no role, no menu key): no office shell
    if (!keys.length) {
        return 'unassigned';
    }
    // Personal keys without a portal key (roles not migrated yet): the previous interface
    return 'office';
}

export function isTeacherPortalPath(path: string): boolean {
    return path === TEACHER_PORTAL_PREFIX || path.startsWith(`${TEACHER_PORTAL_PREFIX}/`);
}

export function isNoAccessPath(path: string): boolean {
    return path === NO_ACCESS_HOME;
}

/** Login without office shell (Lehrkraft-/Bewerbungs-Bereich or nothing granted): app bar without search / menu */
export function hidesOfficeShell(mode: PortalMode): boolean {
    return mode === 'teacher' || mode === 'applicant' || mode === 'unassigned';
}

export function isApplicantPortalPath(path: string): boolean {
    return path === APPLICANT_PORTAL_PREFIX || path.startsWith(`${APPLICANT_PORTAL_PREFIX}/`);
}

export type PortalRouteMeta = { portal?: 'teacher' | 'applicant'; forAll?: boolean; guestOnly?: boolean };

/**
 * Portal part of the route access (after the general checks of routeAccess.ts):
 * true = allowed, string = redirect target (silent, no snackbar).
 */
export function resolvePortalRoute(mode: PortalMode, path: string, meta: PortalRouteMeta, hasTeacherKey: boolean): true | string {
    if (meta.guestOnly) {
        return true;
    }
    if (mode === 'unassigned') {
        // only public / answer pages (above: guestOnly) and the own page
        return meta.forAll || path === NO_ACCESS_HOME ? true : NO_ACCESS_HOME;
    }
    if (meta.portal === 'teacher') {
        if (hasTeacherKey && mode !== 'applicant' && mode !== 'none') {
            return true;
        }
        return mode === 'applicant' ? APPLICANT_PORTAL_HOME : '/';
    }
    if (meta.portal === 'applicant') {
        if (mode === 'applicant') {
            return true;
        }
        return mode === 'teacher' ? TEACHER_PORTAL_HOME : '/';
    }
    // Public / token pages (answer pages from WhatsApp or mail, legal pages, 404) stay open for everybody
    if (meta.forAll) {
        return true;
    }
    if (mode === 'teacher') {
        return TEACHER_PORTAL_HOME;
    }
    if (mode === 'applicant') {
        return APPLICANT_PORTAL_HOME;
    }
    return true;
}
